import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Send, Check, Coffee, Film, Users, TreePine, Wrench } from "lucide-react";

const THEMES = [
  { id: "coffee-tasting", label: "Coffee tasting & conversation", icon: Coffee },
  { id: "watch-party", label: "Watch parties", icon: Film },
  { id: "nature-walk", label: "Nature walks", icon: TreePine },
  { id: "elder-getaway", label: "Senior getaways", icon: Users },
  { id: "lifeskill", label: "Lifeskill building", icon: Wrench },
];
const CITIES = ["Hyderabad", "Bangalore", "Pune"];

export default function InterestForm({ defaultCategory = "coffee-tasting", defaultCity = "Hyderabad", compact = false }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    category: defaultCategory, city: defaultCity, comments: "",
    for_whom: "myself",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("A name and a phone number, please.");
      return;
    }
    setSending(true);
    try {
      await api.post("/interests", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        category: form.category,
        city: form.city,
        comments: form.comments.trim() || null,
        for_whom: form.for_whom,
      });
      setDone(true);
      toast.success("Got it. We'll reach out when we host one near you.");
    } catch { toast.error("Something went wrong. Try again."); }
    finally { setSending(false); }
  };

  if (done) {
    return (
      <div data-testid="interest-success" className="form-card text-center">
        <div className="mx-auto mb-5 flex items-center justify-center rounded-full"
             style={{ width: 64, height: 64, background: "var(--tile-bg)", color: "var(--highlight)" }}>
          <Check size={32} />
        </div>
        <h3 className="font-serif text-3xl leading-tight">You're on the list.</h3>
        <p className="mt-3 text-sm" style={{ color: "var(--text-muted)" }}>
          We'll reach out personally the next time we host a {THEMES.find((t) => t.id === form.category)?.label.toLowerCase()} in {form.city}.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="interest-form"
      className="form-card space-y-5"
    >
      {!compact && (
        <>
          <div className="label-eyebrow">Tell us</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight">
            What would you love to be invited to?
          </h2>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            Three cities. Five kinds of gatherings. Drop your details and we'll personally reach out when we host one nearby.
          </p>
        </>
      )}

      <div>
        <div className="label-eyebrow mb-3">What sounds good to you?</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2" data-testid="theme-picker">
          {THEMES.map((t) => {
            const active = form.category === t.id;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`theme-${t.id}`}
                data-active={active}
                onClick={() => setForm({ ...form, category: t.id })}
                className="tile-select"
              >
                <t.icon size={20} />
                <div className="font-serif text-lg mt-2 leading-snug">{t.label}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="label-eyebrow mb-3">Which city are you in?</div>
        <div className="grid grid-cols-3 gap-2" data-testid="city-picker">
          {CITIES.map((c) => {
            const active = form.city === c;
            return (
              <button
                key={c}
                type="button"
                data-testid={`pick-city-${c.toLowerCase()}`}
                data-active={active}
                onClick={() => setForm({ ...form, city: c })}
                className="pill-select"
              >{c}</button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="label-eyebrow mb-3">Who is this for?</div>
        <div className="grid grid-cols-2 gap-2" data-testid="for-whom-picker">
          {[
            { id: "myself", label: "Me" },
            { id: "someone-i-look-after", label: "Someone I care for" },
          ].map((w) => {
            const active = form.for_whom === w.id;
            return (
              <button
                key={w.id}
                type="button"
                data-testid={`for-${w.id}`}
                data-active={active}
                onClick={() => setForm({ ...form, for_whom: w.id })}
                className="pill-select"
              >{w.label}</button>
            );
          })}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="form-name" />
        <input type="tel" placeholder="Phone number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="form-phone" />
      </div>
      <input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email" />
      <textarea
        rows={3}
        placeholder="Anything specific you'd love? We're listening."
        value={form.comments}
        onChange={(e) => setForm({ ...form, comments: e.target.value })}
        data-testid="form-comments"
      />
      <button type="submit" disabled={sending} className="btn-primary w-full justify-center" data-testid="form-submit">
        {sending ? "Sending…" : "I'm Interested"} <Send size={16} />
      </button>
      <p className="fine-note text-center">
        We'll only use this to invite you. No mailing list, no spam.
      </p>
    </form>
  );
}
